// components/AssignPlanModal.jsx
import React from 'react';
import { X, Users, Package, Gift, DollarSign } from 'lucide-react';

const AssignPlanModal = ({ isOpen, onClose, formState, plans, onChange, onSubmit, isSubmitting }) => {
  if (!isOpen) return null;

  const selectedPlan = plans.find(p => p.planId === formState.planId);
  const planPrice = selectedPlan?.pricing?.yearly?.amount || 0;
  const planHasTrial = selectedPlan?.trial?.enabled || false;
  const planTrialDays = selectedPlan?.trial?.durationDays || 14;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Assign Plan to Tenant</h2>
            <p className="text-sm text-slate-500">Manually assign a subscription</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        {/* Body */}
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" />
                Tenant ID *
              </span>
            </label>
            <input
              type="text"
              name="tenantId"
              value={formState.tenantId}
              onChange={onChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
              placeholder="Enter tenant ID"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <span className="flex items-center gap-2">
                <Package className="w-4 h-4 text-indigo-500" />
                Plan *
              </span>
            </label>
            <select
              name="planId"
              value={formState.planId}
              onChange={onChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
              required
            >
              <option value="">Select a plan</option>
              {plans.filter(p => p.isActive).map((plan) => (
                <option key={plan.planId || plan._id} value={plan.planId}>
                  {plan.name} - {plan.pricing?.yearly?.amount === 0 ? 'Free' : `₹${plan.pricing?.yearly?.amount}/year`}
                  {plan.trial?.enabled && ` (${plan.trial.durationDays}d trial)`}
                </option>
              ))}
            </select>
          </div>

          {selectedPlan && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Plan Price</span>
                <span className="font-semibold text-slate-900">
                  {planPrice === 0 ? 'Free' : `₹${planPrice.toLocaleString()}/year`}
                </span>
              </div>
              {planHasTrial && (
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-slate-600">Trial Available</span>
                  <span className="text-blue-600 font-medium">{planTrialDays} days</span>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              name="status"
              value={formState.status}
              onChange={onChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            >
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Billing Cycle</label>
            <select
              name="billingCycle"
              value={formState.billingCycle}
              onChange={onChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <span className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                Special Price (Optional)
              </span>
            </label>
            <input
              type="number"
              name="specialPrice"
              min="0"
              step="1"
              value={formState.specialPrice}
              onChange={onChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
              placeholder="Leave empty for default price"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                name="isTrial"
                checked={formState.isTrial}
                onChange={onChange}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              <Gift className="w-4 h-4 text-blue-500" />
              Enable Trial
            </label>
          </div>

          {formState.isTrial && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Trial Duration (Days)</label>
              <input
                type="number"
                name="trialDays"
                min="1"
                value={formState.trialDays}
                onChange={onChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
              />
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Assigning...' : 'Assign Subscription'}
            </button>
            <button onClick={onClose} className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignPlanModal;